import React from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { 
  Calculator,
  ArrowRight,
  Home,
  CheckCircle,
  Star,
  Move,
  Volume2,
  Maximize2,
  Phone
} from 'lucide-react';
import { Link } from 'react-router-dom';

const FittedSlidingWardrobesPage = () => {
  const slidingWardrobeStyles = [
    {
      name: 'Linea Sliding',
      subtitle: 'Handleless Elegance',
      description: 'Our Linea sliding doors provide a clean, handleless finish that\'s eye-catching and elegant. Perfect for modern bedrooms where space is premium.',
      image: 'https://static.prod-images.emergentagent.com/jobs/b91ea611-2364-4d55-8415-0e2be2797319/images/3ad24081315c82e8441c5431ac32334c66635adc8d92beaa733c307ff91165fe.png',
      features: [
        'Handleless sliding doors',
        'Clean minimalist lines',
        'Natural Grey & Dust Grey',
        'Soft Cream & Cashmere',
        'Reed Green available',
        'Smooth sliding mechanism'
      ],
      priceRange: 'From £1,800',
      color: 'text-[#8CB89F]'
    },
    {
      name: 'Mirror Sliding',
      subtitle: 'Space Enhancing',
      description: 'Mirror sliding doors that double your room\'s apparent size while providing seamless storage. Perfect for smaller bedrooms and spaces.',
      image: 'https://static.prod-images.emergentagent.com/jobs/b91ea611-2364-4d55-8415-0e2be2797319/images/587d19c8b4573aed50340d8e079badc98afc137cb37f38b7d447fc336fc69c5d.png',
      features: [
        'Full-height mirrors',
        'Space-enhancing illusion',
        'Safety backing film',
        'Smooth soft-close',
        'Beveled edge options',
        'Easy maintenance'
      ],
      priceRange: 'From £2,200',
      color: 'text-purple-600'
    },
    {
      name: 'Mixed Panel Sliding',
      subtitle: 'Versatile Design',
      description: 'Combine mirrors, solid panels, and glass for the ultimate in flexible design. Create unique combinations that perfectly suit your style.',
      image: 'https://static.prod-images.emergentagent.com/jobs/b91ea611-2364-4d55-8415-0e2be2797319/images/79f2e9a3e46d63c12d78f97eafcab5112c7aa1e3bfaeb76a3fc34cf30ef86b44.png',
      features: [
        'Mix of materials',
        'Mirror & solid combinations',
        'Glass panel options',
        'Custom configurations',
        'All finish colors available',
        'Bespoke design service'
      ],
      priceRange: 'From £2,000',
      color: 'text-green-600'
    }
  ];

  const slidingBenefits = [
    {
      icon: Move,
      title: 'Space Saving',
      description: 'No door swing space needed - perfect for tight bedrooms and awkward layouts'
    },
    {
      icon: Volume2,
      title: 'Whisper Quiet',
      description: 'Premium soft-close sliding mechanisms operate silently and smoothly'
    },
    {
      icon: Maximize2,
      title: 'Maximum Access',
      description: '50% of wardrobe contents always accessible with wide sliding openings'
    },
    {
      icon: Star,
      title: 'Modern Aesthetic',
      description: 'Contemporary sliding doors create sleek, uninterrupted bedroom lines'
    }
  ];

  const slidingFeatures = [
    {
      feature: 'Track System',
      description: 'Premium German-engineered sliding tracks',
      specs: ['Top-hung system', 'Weight capacity 80kg', 'Soft-close mechanism']
    },
    {
      feature: 'Door Panels',
      description: '18mm thick panels in all finishes',
      specs: ['MFC construction', 'Edged all round', 'Custom sizes available']
    },
    {
      feature: 'Interior Options',
      description: 'Fully customizable internal layout',
      specs: ['Hanging rails', 'Adjustable shelves', 'Drawer systems']
    },
    {
      feature: 'Installation',
      description: 'Professional fitting service',
      specs: ['Full survey included', '2-year guarantee', 'Tidy installation']
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-purple-50 to-pink-50 py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="lg:flex items-center gap-12">
            <div className="lg:w-1/2 mb-8 lg:mb-0">
              <div className="flex items-center gap-2 mb-4">
                <Move className="h-6 w-6" style={{color: '#8CB89F'}} />
                <Badge className="text-white" style={{backgroundColor: '#8CB89F'}}>Space Saving</Badge>
              </div>
              <h1 className="text-5xl font-bold text-gray-900 mb-6">
                Fitted Sliding Wardrobes
                <span className="block" style={{color: '#8CB89F'}}>Modern Space Solutions</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                <strong>Aston Lily Joinery</strong> specializes in space-saving sliding wardrobes that maximize storage without compromising on style. Perfect for modern bedrooms.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/calculator">
                  <Button size="lg" className="text-white hover:opacity-90 px-8 py-4 text-lg" style={{backgroundColor: '#8CB89F'}}>
                    <Calculator className="mr-2 h-5 w-5" />
                    Get Sliding Quote
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="px-8 py-4 text-lg border-2"
                    style={{borderColor: '#8CB89F', color: '#8CB89F'}}
                  >
                    <Phone className="mr-2 h-5 w-5" />
                    Space Planning
                  </Button>
                </Link>
              </div>
            </div>
            <div className="lg:w-1/2">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/b91ea611-2364-4d55-8415-0e2be2797319/images/3ad24081315c82e8441c5431ac32334c66635adc8d92beaa733c307ff91165fe.png"
                alt="Fitted Sliding Wardrobes"
                className="w-full h-96 object-cover rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16" style={{backgroundColor: '#8CB89F10'}}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose Sliding Wardrobes?</h2>
            <p className="text-xl text-gray-600">
              Modern solutions for contemporary bedroom storage
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {slidingBenefits.map((benefit, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-8 text-center">
                  <benefit.icon className="h-12 w-12 mx-auto mb-4" style={{color: '#8CB89F'}} />
                  <h3 className="text-xl font-semibold mb-3">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Sliding Door Styles */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Sliding Door Styles</h2>
            <p className="text-xl text-gray-600">
              Choose from our range of sliding door options
            </p>
          </div>
          
          <div className="space-y-20">
            {slidingWardrobeStyles.map((style, index) => (
              <div key={index} className={`${index % 2 === 1 ? 'lg:flex-row-reverse' : ''} lg:flex items-center gap-12`}>
                <div className="lg:w-1/2 mb-8 lg:mb-0">
                  <img 
                    src={style.image} 
                    alt={style.name}
                    className="w-full h-96 object-cover rounded-2xl shadow-xl"
                  />
                </div>
                <div className="lg:w-1/2">
                  <div className="flex items-center gap-2 mb-4">
                    <Badge variant="outline" className={style.color}>{style.subtitle}</Badge>
                  </div>
                  
                  <h3 className="text-4xl font-bold text-gray-900 mb-4">{style.name}</h3>
                  <p className="text-lg text-gray-600 mb-6 leading-relaxed">{style.description}</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                    {style.features.map((feature, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <div className="flex items-center gap-4 mb-6">
                    <div className="flex items-center gap-2">
                      <Home className="h-5 w-5 text-[#8CB89F]" />
                      <span className="font-semibold text-lg">{style.priceRange}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technical Features */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Technical Features</h2>
            <p className="text-xl text-gray-600">
              Premium components for smooth, reliable operation
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {slidingFeatures.map((item, index) => (
              <Card key={index} className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-lg">{item.feature}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-3">{item.description}</p>
                  <div className="space-y-1">
                    {item.specs.map((spec, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <CheckCircle className="h-3 w-3 text-green-600" />
                        <span className="text-xs text-gray-600">{spec}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 text-white" style={{backgroundColor: '#8CB89F'}}>
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Transform Your Bedroom with Sliding Wardrobes</h2>
          <p className="text-xl mb-8 opacity-90">
            Get expert space planning and a free quote for your bespoke sliding wardrobes from Aston Lily Joinery.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/calculator">
              <Button size="lg" className="bg-white hover:bg-gray-100 px-8 py-4 text-lg" style={{color: '#8CB89F'}}>
                <Calculator className="mr-2 h-5 w-5" />
                Get Sliding Wardrobe Quote
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white text-white hover:bg-white px-8 py-4 text-lg"
              >
                <Phone className="mr-2 h-5 w-5" />
                Space Planning Consultation
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FittedSlidingWardrobesPage;